import React, { type FunctionComponent } from 'react';

import { mdiLogout } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import AdminSettingsContainer from './AdminSettingsContainer';
import { AdminTabs } from './config/adminSettingsConfig';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { getUrls } from '../../utils/url';

const NavContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-column: 1 / 2;
    grid-row: 2 / 3;
    padding: 1rem;
    background-color: ${(props) => props.theme.colors.white};
`;

const BottomControls = styled.div`
    width: 100%;
    margin-top: auto;
`;

const StyledTabs = styled(Tabs)`
    width: 100%;
`;

const StyledTab = styled(Tab)`
    font-size: 14px;
    text-transform: none;
    align-items: start;
`;
StyledTab.defaultProps = {
    disableRipple: true,
    disableTouchRipple: true
};

const StyledButton = styled(Button)`
    color: ${(props) => props.theme.colors.secondary.lighter};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    font-size: 14px;

    svg {
        width: 24px;
        margin-right: 0.5rem;
        color: inherit;
    }
`;

const SettingsNav: FunctionComponent = () => {
    const { activeTab, setActiveTab, showOrgType } = useContainer(AdminSettingsContainer);
    const { isSuperAdmin } = useContainer(AdminAuthContainer);
    const { t } = useTranslation('admin_settings', { useSuspense: false });

    const history = useHistory();
    const { path } = useRouteMatch();

    const { data: orgSettings } = useOrgSettings(true);

    const { usageStats } = useContainer(AdminSettingsContainer);
    const showRecordingsTab = usageStats && (usageStats.quotaSize > 0 || isSuperAdmin);
    const handleChange = (value) => {
        setActiveTab(value);
        history.push(`${path}${value}`);
    };

    const logout = () => {
        const { vanityUrl } = getUrls();

        localStorage.removeItem('user_uuid');
        window.location.href = vanityUrl;
    };

    return (
        <NavContainer>
            <StyledTabs
                value={activeTab}
                onChange={(_event, value) => handleChange(value)}
                orientation="vertical"
                indicatorColor="primary"
                textColor="primary"
            >
                {showOrgType && <StyledTab value={AdminTabs.General} label={t('navigationTabs.general')} />}
                <StyledTab value={AdminTabs.Features} label={t('navigationTabs.configuration')} />
                <StyledTab value={AdminTabs.Terminology} label={t('navigationTabs.terminology')} />
                <StyledTab value={AdminTabs.Auth} label={t('navigationTabs.auth')} />
                {orgSettings?.featureSettings.options.hasSSOFeature && (
                    <StyledTab value={AdminTabs.Sso} label={t('navigationTabs.sso')} />
                )}
                <StyledTab value={AdminTabs.Classes} label={t('navigationTabs.classes')} />
                <StyledTab value={AdminTabs.Users} label={t('navigationTabs.users')} />
                <StyledTab value={AdminTabs.Integrations} label={t('navigationTabs.integrations')} />
                <StyledTab value={AdminTabs.ApiKeys} label={t('navigationTabs.apiKeys')} />
                <StyledTab value={AdminTabs.Networking} label={t('navigationTabs.networking')} />
                {showRecordingsTab && (
                    <StyledTab value={AdminTabs.RecordingsTab} label={t('navigationTabs.recordings')} />
                )}
                {isSuperAdmin && <StyledTab value={AdminTabs.SuperAdmin} label={t('navigationTabs.super-admin')} />}
            </StyledTabs>

            <BottomControls>
                <StyledButton fullWidth onClick={logout}>
                    <Icon path={mdiLogout} />
                    {t('auth.logout')}
                </StyledButton>
            </BottomControls>
        </NavContainer>
    );
};

export default SettingsNav;
